import { httpClient } from '@shared/api/http-client';
import type { User, LoginCredentials, RegisterData, AuthConfig } from '@shared/types/auth';

export type AuthStatus =
  | 'unknown'
  | 'anonymous'
  | 'authenticated'
  | 'expired'
  | 'forbidden'
  | 'network_error';

type RoleMode = 'any' | 'all';

export interface AuthRedirectOptions {
  redirect?: boolean;
  message?: string;
  returnPath?: string;
}

/**
 * 现代化的用户认证存储和管理
 * 提供响应式状态管理、自动刷新、安全存储等功能
 */
export class AuthStore {
  private user: User | null = null;
  private token: string | null = null;
  private status: AuthStatus = 'unknown';
  private subscribers: Set<(user: User | null) => void> = new Set();
  private statusSubscribers: Set<(status: AuthStatus, user: User | null) => void> = new Set();
  private config: AuthConfig;
  private authReadyPromise: Promise<void>;
  private authReadyResolver!: () => void;
  private isHandlingAuthExpiry = false;

  constructor(config: Partial<AuthConfig> = {}) {
    this.config = {
      tokenKey: 'auth_token',
      userKey: 'current_user',
      refreshTokenKey: 'refresh_token',
      loginPath: '/pages/auth/login.html',
      unauthorizedPath: '/pages/auth/unauthorized.html',
      defaultRedirectPath: '/index.html',
      ...config,
    };

    this.authReadyPromise = new Promise(resolve => {
      this.authReadyResolver = resolve;
    });

    this.setupHttpInterceptors();
    this.initializeAuth();
  }

  /**
   * 初始化认证状态
   */
  private initializeAuth(): void {
    try {
      const storedToken = localStorage.getItem(this.config.tokenKey);

      if (!storedToken) {
        this.status = 'anonymous';
        this.notify();
        this.authReadyResolver();
        return;
      }

      this.token = storedToken;
      this.status = 'unknown';

      // 不在后端验证前恢复 cached user，避免过期 token 刷新后短暂显示为已登录。
      this.notify();

      this.fetchUserProfile().finally(() => {
        this.authReadyResolver();
      });
    } catch (error) {
      console.warn('Failed to initialize auth from storage:', error);
      this.clearAuth('anonymous');
      this.authReadyResolver();
    }
  }

  /**
   * 设置 HTTP 拦截器
   */
  private setupHttpInterceptors(): void {
    // 请求拦截器：自动添加认证头
    httpClient.addRequestInterceptor(config => {
      if (this.token && !config.headers?.Authorization) {
        config.headers = {
          ...config.headers,
          Authorization: `Bearer ${this.token}`,
        };
      }
      return config;
    });

    // 响应拦截器：处理认证失败。httpClient 会在抛错前执行该拦截器。
    httpClient.addResponseInterceptor(async response => {
      if (response.skipAuthErrorHandling) {
        return response;
      }

      if (response.status === 401) {
        await this.expireSession({
          redirect: true,
          message: '登录已过期，请重新登录',
        });
      } else if (response.status === 403 && this.isAuthenticated()) {
        this.redirectToUnauthorized();
      }

      return response;
    });
  }

  /**
   * 等待首次认证状态校验完成
   */
  async ensureReady(): Promise<void> {
    await this.authReadyPromise;
  }

  /**
   * 订阅用户状态变化
   */
  subscribe(callback: (user: User | null) => void): () => void {
    this.subscribers.add(callback);
    // 立即调用一次以获取当前状态
    callback(this.user);

    // 返回取消订阅函数
    return () => {
      this.subscribers.delete(callback);
    };
  }

  /**
   * 订阅认证状态变化
   */
  subscribeStatus(callback: (status: AuthStatus, user: User | null) => void): () => void {
    this.statusSubscribers.add(callback);
    callback(this.status, this.user);

    return () => {
      this.statusSubscribers.delete(callback);
    };
  }

  /**
   * 通知所有订阅者
   */
  private notify(): void {
    this.subscribers.forEach(callback => callback(this.user));
    this.statusSubscribers.forEach(callback => callback(this.status, this.user));
  }

  /**
   * 获取当前用户
   */
  getCurrentUser(): User | null {
    return this.user;
  }

  /**
   * 获取当前认证状态
   */
  getStatus(): AuthStatus {
    return this.status;
  }

  /**
   * 获取当前用户邮箱
   */
  getCurrentEmail(): string | null {
    return this.user?.email || null;
  }

  /**
   * 检查用户是否有特定角色
   */
  hasRole(role: string): boolean {
    return this.user?.roles?.includes(role) ?? false;
  }

  /**
   * 检查用户是否满足角色规则
   */
  hasRoles(roles: string[], mode: RoleMode = 'any'): boolean {
    const normalizedRoles = roles.map(role => role.trim()).filter(Boolean);
    if (normalizedRoles.length === 0) return true;

    return mode === 'all'
      ? normalizedRoles.every(role => this.hasRole(role))
      : normalizedRoles.some(role => this.hasRole(role));
  }

  /**
   * 检查用户是否已认证
   */
  isAuthenticated(): boolean {
    return this.status === 'authenticated' && !!(this.user && this.token);
  }

  /**
   * 登录
   */
  async login(credentials: LoginCredentials): Promise<User> {
    try {
      const response = await httpClient.post<{ user: User; token: string }>(
        '/api/login',
        {
          email: credentials.email,
          password: credentials.password,
          captcha: credentials.captcha,
        },
        { skipAuthErrorHandling: true }
      );

      if (response.success && response.data && response.data.user) {
        await this.setAuthData(response.data.user, response.data.token);
        return response.data.user;
      }

      throw new Error(response.message || 'Login failed');
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  }

  /**
   * 注册
   */
  async register(data: RegisterData): Promise<User> {
    try {
      const response = await httpClient.post<{ user: User; token: string }>('/api/register', data, {
        skipAuthErrorHandling: true,
      });

      // httpClient 现在会自动处理嵌套数据结构
      // response.data 直接是 {user: User, token: string}
      if (response.success && response.data && response.data.user) {
        await this.setAuthData(response.data.user, response.data.token);
        return response.data.user;
      }

      throw new Error(response.message || 'Registration failed');
    } catch (error) {
      console.error('Registration error:', error);
      throw error;
    }
  }

  /**
   * 登出
   */
  async logout(): Promise<void> {
    try {
      await httpClient.post('/api/logout', undefined, { skipAuthErrorHandling: true });
    } catch (error) {
      console.warn('Logout request failed:', error);
    } finally {
      this.clearAuth('anonymous');
      window.location.href = this.config.defaultRedirectPath;
    }
  }

  /**
   * 获取用户信息
   */
  async fetchUserProfile(): Promise<User | null> {
    try {
      const hadToken = !!this.token;
      const response = await httpClient.get<User | null>('/api/profile', undefined, {
        skipAuthErrorHandling: true,
      });

      // httpClient现在会自动处理嵌套数据结构
      // response.data 直接是用户对象或null
      if (response.success) {
        // 如果data为null，说明用户未登录；本地仍有token时必须清理旧登录态
        if (!response.data) {
          this.clearAuth(hadToken ? 'expired' : 'anonymous');
          return null;
        }

        // response.data 直接是用户对象
        const user: User = {
          id: response.data.id || 0,
          email: response.data.email,
          roles: response.data.roles || [],
          name: response.data.name,
          avatar: response.data.avatar,
          createdAt: response.data.createdAt,
          lastLoginAt: response.data.lastLoginAt,
        };

        this.setUser(user);
        return user;
      }

      return null;
    } catch (error) {
      // 网络错误等无法确认认证状态，不清理本地状态，避免误伤用户
      console.warn('Failed to fetch user profile:', error);
      this.status = this.token ? 'network_error' : 'anonymous';
      this.notify();
      return null;
    }
  }

  /**
   * 设置认证数据
   */
  private async setAuthData(user: User, token: string): Promise<void> {
    this.user = user;
    this.token = token;
    this.status = 'authenticated';
    this.isHandlingAuthExpiry = false;

    // 安全存储
    localStorage.setItem(this.config.tokenKey, token);
    localStorage.setItem(this.config.userKey, JSON.stringify(user));

    this.notify();
  }

  /**
   * 设置用户信息
   */
  private setUser(user: User): void {
    this.user = user;
    this.status = 'authenticated';
    localStorage.setItem(this.config.userKey, JSON.stringify(user));
    this.notify();
  }

  /**
   * 清除认证信息
   */
  private clearAuth(status: AuthStatus = 'anonymous'): void {
    this.user = null;
    this.token = null;
    this.status = status;

    localStorage.removeItem(this.config.tokenKey);
    localStorage.removeItem(this.config.userKey);
    if (this.config.refreshTokenKey) {
      localStorage.removeItem(this.config.refreshTokenKey);
    }

    this.notify();
  }

  /**
   * 统一处理登录过期/认证失败
   */
  async expireSession(options: AuthRedirectOptions = {}): Promise<void> {
    if (this.isHandlingAuthExpiry) return;
    this.isHandlingAuthExpiry = true;

    this.clearAuth('expired');

    if (options.redirect === false || this.isOnLoginPage()) {
      return;
    }

    const currentPath = encodeURIComponent(
      options.returnPath || window.location.pathname + window.location.search
    );
    const reason = options.message ? '&reason=expired' : '';
    window.location.href = `${this.config.loginPath}?return=${currentPath}${reason}`;
  }

  /**
   * 强制要求特定角色
   */
  async requireRole(role: string): Promise<void> {
    await this.requireRoles([role], 'any');
  }

  /**
   * 强制要求一组角色
   */
  async requireRoles(roles: string[], mode: RoleMode = 'any'): Promise<void> {
    const isAllowed = await this.requireAuth();
    if (!isAllowed) return;

    if (!this.hasRoles(roles, mode)) {
      this.status = 'forbidden';
      this.notify();

      this.redirectToUnauthorized(roles);
    }
  }

  /**
   * 强制要求登录
   */
  async requireAuth(): Promise<boolean> {
    await this.ensureReady();

    if (this.status === 'unknown' || this.status === 'network_error') {
      await this.fetchUserProfile();
    }

    if (!this.isAuthenticated()) {
      await this.expireSession({ redirect: true, message: '登录已过期，请重新登录' });
      return false;
    }

    return true;
  }

  private redirectToUnauthorized(roles: string[] = []): void {
    this.status = 'forbidden';
    this.notify();

    const currentPath = encodeURIComponent(window.location.pathname + window.location.search);
    const roleParam = roles.length > 0 ? `role=${encodeURIComponent(roles.join(','))}&` : '';
    window.location.href = `${this.config.unauthorizedPath}?${roleParam}return=${currentPath}`;
  }

  private isOnLoginPage(): boolean {
    return window.location.pathname === this.config.loginPath;
  }
}

// 创建全局认证存储实例
export const authStore = new AuthStore();
